"use client";

import { motion } from "framer-motion";

// ============================================
// CODE STRUCTURE GUIDE
// ============================================
//
// This file creates a flowchart with 3 rows in a snake pattern.
//
// LAYOUT:
//   Row 1: [Green Circle] → ONBOARDING → BRAND ANALYSIS ↘
//   Row 2:                  SCHEDULE ← REVIEW & REFINE ← CONTENT GENERATION
//   Row 3: (curved from above) AUTO-PUBLISH → TRACK & OPTIMIZE → ...
//
// SECTIONS:
//   1. benefits - Array of 3 benefit texts for bottom columns
//   2. ProcessDiagram component - Main section
//      - Header (title, subtitle)
//      - Desktop SVG flowchart (all elements inside SVG for proper scaling)
//      - Mobile SVG flowchart (vertical snake pattern)
//      - Benefits grid at bottom
//
// KEY POSITIONING (Desktop SVG viewBox: "0 0 900 400"):
//   - Row 1 Y center: 60
//   - Row 2 Y center: 200
//   - Row 3 Y center: 340
//   - Pills are rect+text inside SVG (scale with container)
//
// TO MODIFY:
//   - Pills: Change rect x/y/width and text x/y positions
//   - Lines: Change the `d` attribute in SVG <path> elements
//   - SVG path syntax: M = move to, L = line to, Q = quadratic curve
//
// ============================================

const benefits = [
  {
    title: "Effortless Setup",
    description: "Connect your store and let AI learn your brand in minutes.",
  },
  {
    title: "Automated Workflow",
    description: "From content creation to publishing, every step runs seamlessly.",
  },
  {
    title: "Continuous Optimization",
    description: "Track performance and refine your strategy with data-driven insights.",
  },
];

export function ProcessDiagram() {
  return (
    <section className="relative overflow-hidden" style={{ paddingTop: 'clamp(5rem, 12vh, 8rem)', paddingBottom: 'clamp(5rem, 12vh, 8rem)' }}>
      {/* ============================================
          BACKGROUND GRADIENT
          - Centered green glow effect
          - Adjust rgba values to change intensity
          ============================================ */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="w-[1000px] h-[800px]"
          style={{
            background: `radial-gradient(ellipse at 50% 50%, rgba(16, 185, 129, 0.15) 0%, transparent 56%)`,
            filter: "blur(60px)",
          }}
        />
      </div>

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        {/* ============================================
            HEADER SECTION
            - "How It Works" label
            - Main title with gradient text
            - Subtitle
            ============================================ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 min-[500px]:mb-14 md:mb-16"
        >
          <span className="text-accent text-xs min-[375px]:text-sm font-medium uppercase tracking-wider mb-3 min-[375px]:mb-4 block">
            How It Works
          </span>
          <h2 className="text-[22px] min-[375px]:text-2xl min-[500px]:text-[28px] sm:text-3xl md:text-4xl font-bold text-foreground mb-3 min-[375px]:mb-4">
            Automation <span className="text-gradient">made simple</span>
          </h2>
          <p className="text-foreground-secondary text-[13px] min-[375px]:text-sm min-[500px]:text-[15px] sm:text-base max-w-[260px] min-[375px]:max-w-[300px] min-[500px]:max-w-[360px] sm:max-w-[400px] md:max-w-2xl mx-auto leading-relaxed">
            Our streamlined workflow automates your entire content pipeline in 7 simple steps.
          </p>
        </motion.div>

        {/* ============================================
            FLOWCHART - DESKTOP VERSION
            - Hidden on mobile (md:block)
            - All elements (lines + pills) inside SVG for proper scaling
            ============================================ */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="hidden md:block relative mb-20 text-foreground"
        >
          {/* ============================================
              SVG FLOWCHART - All elements integrated
              - viewBox="0 0 900 380" defines coordinate system
              - Pills are SVG rect+text elements (scale with container)

              LAYOUT:
              Row 1 (Y=60): Green circle → ONBOARDING → BRAND ANALYSIS
              Row 2 (Y=200): SCHEDULE ← REVIEW & REFINE ← CONTENT GENERATION
              Row 3 (Y=340): AUTO-PUBLISH → TRACK & OPTIMIZE → ...
              ============================================ */}
          <svg
            className="w-full h-auto"
            viewBox="0 0 900 400"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* ===== ROW 1: GREEN CIRCLE → ONBOARDING → BRAND ANALYSIS ===== */}

            {/* Green start circle */}
            <motion.circle
              cx="75" cy="60" r="18" stroke="#10b981" strokeWidth="2" fill="none"
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.1 }}
              style={{ transformOrigin: "75px 60px" }}
            />
            <motion.circle
              cx="75" cy="60" r="9" fill="#10b981"
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.1 }}
              style={{ transformOrigin: "75px 60px" }}
            />

            {/* Line from green circle to ONBOARDING */}
            <motion.path
              d="M 100 60 L 250 60"
              stroke="currentColor"
              strokeWidth="2"
              strokeOpacity="0.5"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
            />
            <motion.polygon
              points="250,55 260,60 250,65"
              fill="currentColor"
              fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.35 }}
            />

            {/* ONBOARDING pill */}
            <motion.rect
              x="270" y="44" width="130" height="32" rx="16"
              stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 0.3 }}
              style={{ transformOrigin: "335px 60px" }}
            />
            <motion.text
              x="335" y="65" textAnchor="middle" fill="currentColor" fontSize="13" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 0.35 }}
            >ONBOARDING</motion.text>

            {/* Connector + line to BRAND ANALYSIS */}
            <motion.circle
              cx="410" cy="60" r="4" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            />
            <motion.path
              d="M 414 60 L 545 60"
              stroke="currentColor"
              strokeWidth="2"
              strokeOpacity="0.5"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.4 }}
            />
            <motion.polygon
              points="545,55 555,60 545,65"
              fill="currentColor"
              fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            />

            {/* BRAND ANALYSIS pill */}
            <motion.rect
              x="565" y="44" width="145" height="32" rx="16"
              stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 0.45 }}
              style={{ transformOrigin: "637px 60px" }}
            />
            <motion.text
              x="637" y="65" textAnchor="middle" fill="currentColor" fontSize="13" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 0.5 }}
            >BRAND ANALYSIS</motion.text>

            {/* Connector + curve down to Row 2 */}
            <motion.circle
              cx="720" cy="60" r="4" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.55 }}
            />
            <motion.path
              d="M 724 60 L 790 60 Q 850 60 850 130 L 850 130 Q 850 200 790 200 L 778 200"
              stroke="currentColor"
              strokeWidth="2"
              strokeOpacity="0.5"
              fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
            />

            {/* ===== ROW 2: CONTENT GENERATION ← REVIEW & REFINE ← SCHEDULE ===== */}

            {/* Arrow pointing LEFT (entering CONTENT GENERATION) */}
            <motion.polygon
              points="778,195 768,200 778,205"
              fill="currentColor"
              fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.65 }}
            />

            {/* CONTENT GENERATION pill */}
            <motion.rect
              x="600" y="184" width="165" height="32" rx="16"
              stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 0.7 }}
              style={{ transformOrigin: "682px 200px" }}
            />
            <motion.text
              x="682" y="205" textAnchor="middle" fill="currentColor" fontSize="12" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 0.75 }}
            >CONTENT GENERATION</motion.text>

            {/* Connector + line to REVIEW & REFINE */}
            <motion.circle
              cx="590" cy="200" r="4" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
            />
            <motion.path
              d="M 586 200 L 510 200"
              stroke="currentColor"
              strokeWidth="2"
              strokeOpacity="0.5"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.8 }}
            />
            <motion.polygon
              points="510,195 500,200 510,205"
              fill="currentColor"
              fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.85 }}
            />

            {/* REVIEW & REFINE pill */}
            <motion.rect
              x="350" y="184" width="145" height="32" rx="16"
              stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 0.9 }}
              style={{ transformOrigin: "422px 200px" }}
            />
            <motion.text
              x="422" y="205" textAnchor="middle" fill="currentColor" fontSize="13" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 0.95 }}
            >REVIEW & REFINE</motion.text>

            {/* Connector + line to SCHEDULE */}
            <motion.circle
              cx="340" cy="200" r="4" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.95 }}
            />
            <motion.path
              d="M 336 200 L 280 200"
              stroke="currentColor"
              strokeWidth="2"
              strokeOpacity="0.5"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.95 }}
            />
            <motion.polygon
              points="280,195 270,200 280,205"
              fill="currentColor"
              fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.0 }}
            />

            {/* SCHEDULE pill */}
            <motion.rect
              x="155" y="184" width="105" height="32" rx="16"
              stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 1.05 }}
              style={{ transformOrigin: "207px 200px" }}
            />
            <motion.text
              x="207" y="205" textAnchor="middle" fill="currentColor" fontSize="13" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 1.1 }}
            >SCHEDULE</motion.text>

            {/* Connector + curve down to Row 3 */}
            <motion.circle
              cx="145" cy="200" r="4" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.1 }}
            />
            <motion.path
              d="M 141 200 L 120 200 Q 50 200 50 270 L 50 270 Q 50 340 120 340 L 200 340"
              stroke="currentColor"
              strokeWidth="2"
              strokeOpacity="0.5"
              fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 1.1 }}
            />

            {/* ===== ROW 3: AUTO-PUBLISH → TRACK & OPTIMIZE → ... ===== */}

            {/* Arrow pointing RIGHT (entering AUTO-PUBLISH) */}
            <motion.polygon
              points="200,335 210,340 200,345"
              fill="currentColor"
              fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.25 }}
            />

            {/* AUTO-PUBLISH pill */}
            <motion.rect
              x="220" y="324" width="130" height="32" rx="16"
              stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 1.3 }}
              style={{ transformOrigin: "285px 340px" }}
            />
            <motion.text
              x="285" y="345" textAnchor="middle" fill="currentColor" fontSize="13" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 1.35 }}
            >AUTO-PUBLISH</motion.text>

            {/* Connector + line to TRACK & OPTIMIZE */}
            <motion.circle
              cx="360" cy="340" r="4" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.4 }}
            />
            <motion.path
              d="M 364 340 L 470 340"
              stroke="currentColor"
              strokeWidth="2"
              strokeOpacity="0.5"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 1.4 }}
            />
            <motion.polygon
              points="470,335 480,340 470,345"
              fill="currentColor"
              fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.45 }}
            />

            {/* TRACK & OPTIMIZE pill */}
            <motion.rect
              x="490" y="324" width="160" height="32" rx="16"
              stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 1.5 }}
              style={{ transformOrigin: "570px 340px" }}
            />
            <motion.text
              x="570" y="345" textAnchor="middle" fill="currentColor" fontSize="13" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 1.55 }}
            >TRACK & OPTIMIZE</motion.text>

            {/* Connector + continuation line */}
            <motion.circle
              cx="660" cy="340" r="4" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.6 }}
            />
            <motion.path
              d="M 664 340 L 820 340"
              stroke="currentColor"
              strokeWidth="2"
              strokeOpacity="0.5"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 1.6 }}
            />
            <motion.polygon
              points="820,335 830,340 820,345"
              fill="currentColor"
              fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.7 }}
            />
          </svg>
        </motion.div>

        {/* ============================================
            FLOWCHART - MOBILE VERSION
            - Shown on screens below md (md:hidden)
            - SVG-based snake pattern flowchart
            ============================================ */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="md:hidden relative mb-10 max-w-[420px] mx-auto text-foreground"
        >
          <svg
            className="w-full h-auto"
            viewBox="-25 0 370 420"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* ===== ROW 1: GREEN CIRCLE + ONBOARDING ===== */}
            {/* Green start circle */}
            <motion.circle
              cx="30" cy="25" r="14" stroke="#10b981" strokeWidth="2" fill="none"
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.1 }}
              style={{ transformOrigin: "30px 25px" }}
            />
            <motion.circle
              cx="30" cy="25" r="7" fill="#10b981"
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 0.1 }}
              style={{ transformOrigin: "30px 25px" }}
            />

            {/* Line from green circle to ONBOARDING */}
            <motion.line
              x1="50" y1="25" x2="138" y2="25" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.5"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 0.2 }}
            />
            <motion.polygon
              points="138,21 146,25 138,29" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            />

            {/* ONBOARDING pill */}
            <motion.rect
              x="160" y="9" width="125" height="32" rx="16" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 0.35 }}
              style={{ transformOrigin: "222px 25px" }}
            />
            <motion.text
              x="222" y="30" textAnchor="middle" fill="currentColor" fontSize="11" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 0.4 }}
            >ONBOARDING</motion.text>

            {/* Dot + Curve to row 2 */}
            <motion.circle
              cx="295" cy="25" r="3" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.45 }}
            />
            <motion.path
              d="M 298 25 Q 325 25 325 55 L 325 55 Q 325 85 295 85 L 178 85" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.5" fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: 0.5 }}
            />
            <motion.polygon
              points="178,81 170,85 178,89" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.65 }}
            />

            {/* ===== ROW 2: BRAND ANALYSIS ===== */}
            <motion.rect
              x="30" y="69" width="125" height="32" rx="16" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 0.7 }}
              style={{ transformOrigin: "92px 85px" }}
            />
            <motion.text
              x="92" y="90" textAnchor="middle" fill="currentColor" fontSize="11" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 0.75 }}
            >BRAND ANALYSIS</motion.text>

            {/* Dot + Curve to row 3 */}
            <motion.circle
              cx="20" cy="85" r="3" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
            />
            <motion.path
              d="M 17 85 Q -10 85 -10 115 L -10 115 Q -10 145 20 145 L 138 145" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.5" fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: 0.85 }}
            />
            <motion.polygon
              points="138,141 146,145 138,149" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.0 }}
            />

            {/* ===== ROW 3: CONTENT GENERATION ===== */}
            <motion.rect
              x="160" y="129" width="125" height="32" rx="16" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 1.05 }}
              style={{ transformOrigin: "222px 145px" }}
            />
            <motion.text
              x="222" y="150" textAnchor="middle" fill="currentColor" fontSize="10" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 1.1 }}
            >CONTENT GENERATION</motion.text>

            {/* Dot + Curve to row 4 */}
            <motion.circle
              cx="295" cy="145" r="3" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.15 }}
            />
            <motion.path
              d="M 298 145 Q 325 145 325 175 L 325 175 Q 325 205 295 205 L 178 205" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.5" fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: 1.2 }}
            />
            <motion.polygon
              points="178,201 170,205 178,209" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.35 }}
            />

            {/* ===== ROW 4: REVIEW & REFINE ===== */}
            <motion.rect
              x="30" y="189" width="125" height="32" rx="16" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 1.4 }}
              style={{ transformOrigin: "92px 205px" }}
            />
            <motion.text
              x="92" y="210" textAnchor="middle" fill="currentColor" fontSize="11" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 1.45 }}
            >REVIEW & REFINE</motion.text>

            {/* Dot + Curve to row 5 */}
            <motion.circle
              cx="20" cy="205" r="3" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.5 }}
            />
            <motion.path
              d="M 17 205 Q -10 205 -10 235 L -10 235 Q -10 265 20 265 L 138 265" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.5" fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: 1.55 }}
            />
            <motion.polygon
              points="138,261 146,265 138,269" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.7 }}
            />

            {/* ===== ROW 5: SCHEDULE ===== */}
            <motion.rect
              x="160" y="249" width="125" height="32" rx="16" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 1.75 }}
              style={{ transformOrigin: "222px 265px" }}
            />
            <motion.text
              x="222" y="270" textAnchor="middle" fill="currentColor" fontSize="11" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 1.8 }}
            >SCHEDULE</motion.text>

            {/* Dot + Curve to row 6 */}
            <motion.circle
              cx="295" cy="265" r="3" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1.85 }}
            />
            <motion.path
              d="M 298 265 Q 325 265 325 295 L 325 295 Q 325 325 295 325 L 178 325" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.5" fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: 1.9 }}
            />
            <motion.polygon
              points="178,321 170,325 178,329" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 2.05 }}
            />

            {/* ===== ROW 6: AUTO-PUBLISH ===== */}
            <motion.rect
              x="30" y="309" width="125" height="32" rx="16" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 2.1 }}
              style={{ transformOrigin: "92px 325px" }}
            />
            <motion.text
              x="92" y="330" textAnchor="middle" fill="currentColor" fontSize="11" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 2.15 }}
            >AUTO-PUBLISH</motion.text>

            {/* Dot + Curve to row 7 */}
            <motion.circle
              cx="20" cy="325" r="3" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 2.2 }}
            />
            <motion.path
              d="M 17 325 Q -10 325 -10 355 L -10 355 Q -10 385 20 385 L 138 385" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.5" fill="none"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: 2.25 }}
            />
            <motion.polygon
              points="138,381 146,385 138,389" fill="currentColor" fillOpacity="0.5"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 2.4 }}
            />

            {/* ===== ROW 7: TRACK & OPTIMIZE ===== */}
            <motion.rect
              x="160" y="369" width="140" height="32" rx="16" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" fill="none"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.25, delay: 2.45 }}
              style={{ transformOrigin: "230px 385px" }}
            />
            <motion.text
              x="230" y="390" textAnchor="middle" fill="currentColor" fontSize="11" fontWeight="500"
              fontFamily="'Space Grotesk', system-ui, sans-serif"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.2, delay: 2.5 }}
            >TRACK & OPTIMIZE</motion.text>
          </svg>
        </motion.div>

        {/* ============================================
            BENEFITS SECTION
            - Desktop: 3 columns grid
            - Mobile: Alternating gradient strips
            ============================================ */}

        {/* Desktop version - 3 columns */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="hidden md:grid md:grid-cols-3 gap-8 md:gap-12 md:pl-16"
        >
          {benefits.map((benefit, index) => (
            <div key={index} className="text-left" style={{ maxWidth: 'clamp(200px, 18vw, 280px)' }}>
              <h3
                className="text-foreground font-semibold mb-2 whitespace-nowrap"
                style={{ fontSize: 'clamp(0.875rem, 1vw + 0.25rem, 1.125rem)' }}
              >
                {benefit.title}
              </h3>
              <p
                className="text-foreground-secondary leading-relaxed"
                style={{ fontSize: 'clamp(0.8125rem, 0.9vw + 0.125rem, 0.9375rem)' }}
              >
                {benefit.description}
              </p>
            </div>
          ))}
        </motion.div>

        {/* Mobile version - Alternating gradient strips */}
        <div className="md:hidden flex flex-col gap-3 min-[375px]:gap-4 min-[500px]:gap-5 sm:gap-6 -mx-6">
          {benefits.map((benefit, index) => {
            const isFromLeft = index % 2 === 0;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: isFromLeft ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + index * 0.15, duration: 0.4 }}
                className={`relative py-3 min-[375px]:py-4 min-[500px]:py-5 sm:py-6 ${isFromLeft ? 'pl-4 min-[375px]:pl-5 min-[500px]:pl-6 pr-6 mr-8 min-[375px]:mr-12 min-[500px]:mr-16 sm:mr-20 rounded-r-2xl' : 'pr-4 min-[375px]:pr-5 min-[500px]:pr-6 pl-6 ml-8 min-[375px]:ml-12 min-[500px]:ml-16 sm:ml-20 rounded-l-2xl'}`}
                style={{
                  background: isFromLeft
                    ? 'linear-gradient(90deg, rgba(16, 185, 129, 0.18) 0%, rgba(163, 230, 53, 0.06) 60%, transparent 100%)'
                    : 'linear-gradient(270deg, rgba(16, 185, 129, 0.18) 0%, rgba(163, 230, 53, 0.06) 60%, transparent 100%)',
                }}
              >
                {/* Accent line */}
                <div
                  className={`absolute top-0 bottom-0 w-[3px] ${isFromLeft ? 'left-0' : 'right-0'}`}
                  style={{
                    background: 'linear-gradient(180deg, #10b981 0%, #a3e635 100%)',
                  }}
                />
                <h3 className={`text-foreground font-semibold mb-1 min-[375px]:mb-1.5 min-[500px]:mb-2 text-[14px] min-[375px]:text-[15px] min-[500px]:text-base sm:text-[17px] ${isFromLeft ? 'text-left' : 'text-right'}`}>
                  {benefit.title}
                </h3>
                <p className={`text-foreground-secondary text-[12px] min-[375px]:text-[13px] min-[500px]:text-sm sm:text-[15px] leading-relaxed ${isFromLeft ? 'text-left' : 'text-right'}`}>
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

